import moment from 'moment';
import { BinarySyntax, createDateTimeValue, DateTimeValue, DateValue, FunctionCallSyntax, IdentifierSyntax, IndexSyntax, ListSyntax, LiteralSyntax, MapSyntax, MemberSyntax, SyntaxNode, SyntaxVisitor, TimeValue, UnarySyntax } from '../expr';
import { SYMBOLS } from './Symbols';
import { Value } from './Value';
import { VM } from './VM';
import { LiteralType } from 'src/app/services/api.services';

export namespace Compiler {
  export function compile(syntax: SyntaxNode): (vm: VM) => any {
    let buffer: string[] = ['return '];

    new class extends SyntaxVisitor {
      visitUnary(syntax: UnarySyntax) {
        buffer.push('vm.');
        buffer.push(syntax.operator);
        buffer.push('(');
        this.visit(syntax.right);
        buffer.push(')');
      }

      visitBinary(syntax: BinarySyntax) {
        buffer.push('vm.');
        buffer.push(syntax.operator);
        buffer.push('(');
        this.visit(syntax.left);
        buffer.push(', ');
        this.visit(syntax.right);
        buffer.push(')');
      }

      visitFunctionCall(syntax: FunctionCallSyntax) {
        let symbol = SYMBOLS.getFunction(syntax.functionName);
        if (symbol) {
          buffer.push('vm.call(vm.symbols.');
          buffer.push(symbol.name);
          buffer.push(', ...[');
          buffer.push(...syntax.args.map(a => this.visit(a)).join(', '));
          buffer.push('])');
        }
        else {
          buffer.push('vm.call(\'');
          buffer.push(syntax.functionName);
          buffer.push('\', ...[');
          buffer.push(...syntax.args.map(a => this.visit(a)).join(', '));
          buffer.push('])');
        }
      }

      visitIdentifier(syntax: IdentifierSyntax) {
        let symbol = SYMBOLS.getIdentifier(syntax.value);
        if (symbol) {
          buffer.push('vm.ident(vm.symbols.');
          buffer.push(symbol.name);
          buffer.push(')');
        }
        else {
          buffer.push('vm.ident(');
          buffer.push(escapeString(syntax.value));
          buffer.push(')');
        }
      }


      visitIndex(syntax: IndexSyntax) {
        buffer.push('vm.indexList(');
        this.visit(syntax.subject);
        buffer.push(', ');
        this.visit(syntax.key);
        buffer.push(')');
      }

      visitMember(syntax: MemberSyntax) {
        buffer.push('vm.indexMap(');
        this.visit(syntax.subject);
        buffer.push(', ');
        buffer.push(escapeString(syntax.key));
        buffer.push(')');
      }


      visitList(syntax: ListSyntax) {
        buffer.push('vm.makeList(');

        if (syntax.items.length) {
          this.visit(syntax.items[0]);

          for (let i = 1; i < syntax.items.length; ++i) {
            buffer.push(', ');
            this.visit(syntax.items[i]);
          }
        }

        buffer.push(')');
      }

      visitMap(syntax: MapSyntax) {
        buffer.push('vm.makeMap(');

        if (syntax.items.length) {
          this.visit(syntax.items[0].key);
          this.visit(syntax.items[0].value);

          for (let i = 1; i < syntax.items.length; ++i) {
            buffer.push(', ');
            this.visit(syntax.items[i].key);
            this.visit(syntax.items[i].value);
          }
        }

        buffer.push(')');
      }


      visitLiteral(syntax: LiteralSyntax) {
        let v = syntax.value;
        
        if (v instanceof Value) {
          v = v.value;
        }

        if (v === null || v === undefined) {
          buffer.push('NULL');
          return;
        }

        if (syntax.type == LiteralType.String) {
          buffer.push(escapeString(v));
          return;
        }

        if (syntax.type == LiteralType.Boolean) {
          if (!!v) 
            buffer.push('TRUE');
          else 
            buffer.push('FALSE');
            
          return;
        }

        if (syntax.type == LiteralType.Integer
         || syntax.type == LiteralType.Number 
         || syntax.type == LiteralType.Decimal) {
          let int = syntax.type == LiteralType.Integer;

          if (typeof v === 'number') {
            buffer.push((int ? Math.floor(v) : v).toString());
            return;
          }
          
          if (typeof v === 'bigint') {
            buffer.push(BigInt(v).toString());
            return;
          }
          
          if (!(v instanceof BigNumber)) {
            v = BigNumber(v);
          }

          buffer.push((int ? v.integerValue() : v).toString());
          return;
        }
        
        if (v instanceof Date) {
          v = moment(v);
        }

        if (moment.isMoment(v)) {
          v = createDateTimeValue(v, v.tz());
        }

        if (v instanceof DateValue) {
          switch (syntax.type) {
            case LiteralType.DateTime: 
              v = new DateTimeValue(v, TimeValue.midnight, 'utc');
              break;

            case LiteralType.Time: 
              throw new Error(`Unexpected literal value`, { cause: syntax });
          }
        }
        else
        if (v instanceof TimeValue) {
          switch (syntax.type) {
            case LiteralType.Date: 
            case LiteralType.DateTime:
              throw new Error(`Unexpected literal value`, { cause: syntax });
          }
        }
        else
        if (v instanceof DateTimeValue) {
          switch (syntax.type) {
            case LiteralType.Date: 
              v = v.date;
              break;

            case LiteralType.Time: 
              throw new Error(`Unexpected literal value`, { cause: syntax });
          }
        }

        if (typeof v === 'object' && 'toSyntaxString' in v) {
          buffer.push(v.toSyntaxString());
          return;
        }

        throw new Error(`Unexpected literal value`, { cause: syntax });
      }
    }().visit(syntax);

    buffer.push(';');
    let javaScript = buffer.join('');

    let func = new Function('vm', javaScript);
    return <any>func;
  }

  function escapeString(value: string) {
    return `'${value.replace(/'/g, '\\\'')}'`;
  }
}
